import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Check, X, Trophy, TrendingDown, AlertCircle, Truck, DollarSign, RotateCcw, FileText, Zap, FileDown, Eye,
} from "lucide-react";
import { COTACAO_API } from "./api";
import useCotacaoUser from "./useCotacaoUser";

const brl = (v) => v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formaPgtoLabel = (fp) => {
  if (!fp?.tipo) return "—";
  if (fp.tipo === "avista") return "À vista";
  if (fp.tipo === "boleto") return "Boleto";
  if (fp.tipo === "prazo") {
    const dias = (fp.parcelas || []).map((p) => p.dias).join("/");
    return `Prazo ${dias || ""} dias`.trim();
  }
  return fp.tipo;
};

export default function CotacaoMapaPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canWrite, isAuditor } = useCotacaoUser();
  const [mapa, setMapa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // fornecedor_id+produto_id sendo salvo
  const [gerando, setGerando] = useState(false);

  useEffect(() => { carregar(); }, [id]);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get(`/cotacao/cotacoes/${id}/mapa`);
      setMapa(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar mapa");
    } finally { setLoading(false); }
  };

  const gerarPedidos = async () => {
    if (!window.confirm("Isso fechará a cotação e criará 1 pedido por fornecedor ganhador. Continuar?")) return;
    setGerando(true);
    try {
      const { data } = await cotAxios.post(`/cotacao/cotacoes/${id}/gerar-pedidos`);
      toast.success(`${data.pedidos.length} pedido(s) gerado(s) com sucesso!`);
      nav("/cotacoes/dashboard/pedidos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar pedidos");
    } finally { setGerando(false); }
  };

  const decidir = async (produto_id, fornecedor_id, decisao) => {
    const key = `${produto_id}:${fornecedor_id}`;
    setSaving(key);
    try {
      await cotAxios.put(`/cotacao/cotacoes/${id}/itens/${produto_id}/decisao`, { fornecedor_id, decisao });
      toast.success(decisao === "aprovar" ? "Aprovada!" : decisao === "reprovar" ? "Reprovada" : "Pendente");
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setSaving(null); }
  };

  if (loading) return <div className="text-center py-10 text-slate-500">Carregando mapa...</div>;
  if (!mapa) return <div className="text-center py-10 text-slate-500">Cotação não encontrada.</div>;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="cot-mapa-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav(`/cotacoes/dashboard/cotacoes/${id}`)} data-testid="btn-voltar-mapa">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={async () => {
          try {
            const token = localStorage.getItem("cotacao_token");
            const res = await fetch(`${COTACAO_API}/cotacao/cotacoes/${id}/mapa/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `mapa-cotacao-${String(mapa?.numero || 0).padStart(4, "0")}.pdf`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch { toast.error("Erro ao gerar PDF do mapa"); }
        }} data-testid="btn-pdf-mapa">
          <FileDown className="w-4 h-4 mr-1" /> PDF do Mapa
        </Button>
        {mapa?.status !== "fechada" && mapa?.resumo?.itens_aprovados > 0 && canWrite && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={gerarPedidos} disabled={gerando} data-testid="btn-gerar-pedidos">
            <Zap className="w-4 h-4 mr-1" /> {gerando ? "Gerando..." : `Gerar Pedido (${mapa.resumo.itens_aprovados} item(ns))`}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Mapa de Cotação nº {mapa.numero}
            <span className={`px-2 py-1 rounded text-[11px] font-bold ${mapa.status === "fechada" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`} data-testid="badge-status-mapa">
              {(mapa.status || "").toUpperCase()}
            </span>
          </h1>
          {mapa.titulo && <p className="text-slate-600 text-sm">{mapa.titulo}</p>}
          {isAuditor && (
            <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Modo Auditor · somente visualização</p>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Itens aprovados</div>
          <div className="font-semibold text-lg">{mapa.resumo.itens_aprovados}/{mapa.resumo.itens_total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Total aprovado</div>
          <div className="font-semibold text-lg">{brl(mapa.resumo.total_aprovado)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-slate-500">Pior cenário</div>
          <div className="font-semibold text-lg text-red-700">{brl(mapa.resumo.total_pior_cenario)}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4">
          <div className="text-xs text-emerald-800 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Saving referente Maior Custo</div>
          <div className="font-semibold text-lg text-emerald-700">{brl(mapa.resumo.economia_total)}</div>
        </CardContent></Card>
      </div>

      {mapa.fornecedores_pendentes?.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <b className="text-amber-900">{mapa.fornecedores_pendentes.length} fornecedor(es) ainda não responderam:</b>{" "}
            <span className="text-amber-800">{mapa.fornecedores_pendentes.map((f) => f.nome).join(", ")}</span>
          </div>
        </div>
      )}

      {/* Itens */}
      <div className="space-y-6">
        {mapa.itens.map((it) => (
          <Card key={it.produto_id} data-testid={`mapa-item-${it.produto_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="text-lg font-semibold">
                    {it.produto.codigo && <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded mr-1">{it.produto.codigo}</span>}
                    {it.produto.nome}
                  </div>
                  <div className="text-xs text-slate-500">
                    Qtd: <b>{it.qtd} {it.produto.unidade || ""}</b>
                    {it.produto.marca && <> · Marca: {it.produto.marca}</>}
                    {it.produto.referencia && <> · Ref: {it.produto.referencia}</>}
                  </div>
                </div>
                <div className="flex gap-3 text-xs flex-wrap">
                  {it.ultimo_preco != null && (
                    <div className="px-3 py-1 rounded bg-slate-50 border">
                      <span className="text-slate-500">Última compra: </span>
                      <b>{brl(it.ultimo_preco)}</b>
                    </div>
                  )}
                  {it.propostas.length > 1 && (
                    <div className="px-3 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                      Melhor: <b>{brl(it.melhor_preco)}</b>
                    </div>
                  )}
                </div>
              </div>

              {it.propostas.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-4 text-center">Nenhuma proposta recebida ainda para este item.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-2 pr-3">Fornecedor</th>
                        <th className="text-right py-2 pr-3">Preço unit.</th>
                        <th className="text-right py-2 pr-3">Total</th>
                        <th className="text-left py-2 pr-3">Pagamento</th>
                        <th className="text-left py-2 pr-3">Frete</th>
                        <th className="text-right py-2 pr-3">Saving Maior Custo</th>
                        <th className="text-center py-2">Decisão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {it.propostas.map((p) => {
                        const isSaving = saving === `${it.produto_id}:${p.fornecedor_id}`;
                        const isGanhador = p.aprovada === true;
                        const isRejected = p.aprovada === false;
                        const rowCls = isGanhador
                          ? "bg-emerald-50 border-l-4 border-emerald-500"
                          : isRejected
                          ? "opacity-60"
                          : p.melhor
                          ? "bg-emerald-50/40"
                          : "";
                        return (
                          <tr key={p.id} className={`border-b hover:bg-slate-50 ${rowCls}`} data-testid={`mapa-row-${it.produto_id}-${p.fornecedor_id}`}>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{p.fornecedor_nome}</span>
                                {isGanhador && <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold flex items-center gap-1"><Trophy className="w-3 h-3" />GANHADOR</span>}
                                {!isGanhador && p.melhor && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">MELHOR</span>}
                                {p.unico && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">ÚNICO</span>}
                              </div>
                              {p.vendedor && <div className="text-[11px] text-slate-500">{p.vendedor}</div>}
                            </td>
                            <td className="py-2 pr-3 text-right font-semibold">{brl(p.preco)}</td>
                            <td className="py-2 pr-3 text-right">{brl(p.total)}</td>
                            <td className="py-2 pr-3"><span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-slate-400" />{formaPgtoLabel(p.forma_pgto)}</span></td>
                            <td className="py-2 pr-3"><span className="flex items-center gap-1"><Truck className="w-3 h-3 text-slate-400" />{p.frete || "—"}</span></td>
                            <td className="py-2 pr-3 text-right">
                              {p.economia_vs_pior > 0 ? <span className="text-emerald-700 font-medium">{brl(p.economia_vs_pior)}</span> : "—"}
                            </td>
                            <td className="py-2">
                              <div className="flex items-center justify-center gap-1">
                                {canWrite ? (
                                  <>
                                    <Button size="sm" variant={isGanhador ? "default" : "outline"} className={isGanhador ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                      onClick={() => decidir(it.produto_id, p.fornecedor_id, "aprovar")}
                                      disabled={isSaving}
                                      data-testid={`btn-aprovar-${it.produto_id}-${p.fornecedor_id}`}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant={isRejected ? "destructive" : "outline"}
                                      onClick={() => decidir(it.produto_id, p.fornecedor_id, "reprovar")}
                                      disabled={isSaving}
                                      data-testid={`btn-reprovar-${it.produto_id}-${p.fornecedor_id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                    {p.aprovada !== null && (
                                      <Button size="sm" variant="ghost"
                                        onClick={() => decidir(it.produto_id, p.fornecedor_id, "pendente")}
                                        disabled={isSaving}
                                        data-testid={`btn-reset-${it.produto_id}-${p.fornecedor_id}`}
                                        title="Voltar a pendente"
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-slate-400 inline-flex items-center gap-1" title="Auditor: somente visualização">
                                    <Eye className="w-3.5 h-3.5" /> visualização
                                  </span>
                                )}
                              </div>
                              {p.observacoes && (
                                <div className="text-[11px] text-slate-500 text-center mt-1 flex items-center justify-center gap-1">
                                  <FileText className="w-3 h-3" /> {p.observacoes}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
